/**
 * The reference drawing on a shot card.
 *
 * A condition-report view — side, front or top — with **only the area you need
 * to photograph** picked out. Everything else stays thin line work, so the
 * target reads without anyone reading the caption.
 *
 * Line art rather than a photograph, deliberately: it prints on a shop wall, it
 * stays legible whatever vehicle is actually in the bay, and it carries no
 * licensing or privacy baggage — a real VIN photograph is identifying data.
 */

import type { View } from '../capture/shotlist.js';

const BOX: Record<View, string> = {
  side: '0 0 210 78',
  front: '0 0 140 90',
  top: '0 0 150 148',
  rear: '0 0 140 90',
};

const OUTLINE: Record<View, string> = {
  side:
    'M10 58 L10 44 Q10 39 16 38 L60 35 L76 15 Q78 13 82 13 L122 13 Q126 13 127 16 ' +
    'L132 35 L196 35 Q201 36 201 42 L201 58 L176 58 A16 16 0 0 0 144 58 L74 58 ' +
    'A16 16 0 0 0 42 58 Z',
  front:
    'M18 74 L18 42 Q18 32 28 30 L46 27 L54 13 Q56 10 62 10 L78 10 Q84 10 86 13 ' +
    'L94 27 L112 30 Q122 32 122 42 L122 74 Z',
  top:
    'M30 14 Q30 6 42 6 L108 6 Q120 6 120 14 L126 56 L126 138 Q126 143 120 143 ' +
    'L30 143 Q24 143 24 138 L24 56 Z',
  rear:
    'M18 74 L18 42 Q18 32 28 30 L46 27 L54 13 Q56 10 62 10 L78 10 Q84 10 86 13 ' +
    'L94 27 L112 30 Q122 32 122 42 L122 74 Z',
};

/** Extra strokes that make each view read as a vehicle rather than a blob. */
const DETAIL: Record<View, string[]> = {
  side: [
    'M84 33 L84 17 L120 17 L120 33 Z',
    'M84 35 L84 58 M120 35 L120 58',
    'M136 39 L195 39',
    'M12 41 L26 39 L26 46 L12 47 Z',
  ],
  front: [
    'M57 15 L83 15 L90 28 L50 28 Z',
    'M36 44 L104 44 L104 58 L36 58 Z',
    'M20 43 L34 43 L34 55 L21 55 Z',
    'M120 43 L106 43 L106 55 L119 55 Z',
    'M16 60 L124 60 L124 70 L16 70 Z',
  ],
  top: [
    'M26 22 L124 22',                                   // bumper seam
    'M34 26 L116 26 L120 66 L30 66 Z',                  // hood
    'M24 30 L30 30 L28 66 L24 66',                      // LH fender top
    'M126 30 L120 30 L122 66 L126 66',                  // RH fender top
    'M34 70 L116 70 L112 86 L38 86 Z',                  // windscreen
    'M38 90 L112 90 L112 122 L38 122 Z',                // roof
    'M38 126 L112 126 L112 138 L38 138 Z',              // rear deck
    'M24 74 L16 78 L16 84 L24 82 Z',                    // mirrors
    'M126 74 L134 78 L134 84 L126 82 Z',
  ],
  rear: [
    'M57 15 L83 15 L90 28 L50 28 Z',
    'M16 60 L124 60 L124 70 L16 70 Z',
  ],
};

/**
 * Where the highlight sits, per shot id.
 *
 * Keyed by shot rather than by zone because the drawing answers a different
 * question from the data model: *where do I point the camera*, not *what part is
 * this*. A loupe ring is added for small targets — a highlighted twelve-pixel
 * rectangle does not read as "look here".
 */
const TARGET: Record<string, { view: View; shapes: string; loupe?: [number, number, number] }> = {
  VIN:        { view: 'side',  shapes: 'M76 15 Q78 13 82 13 L86 13 L82 33 L68 33 Z', loupe: [78, 23, 14] },
  PAINT:      { view: 'side',  shapes: 'M84 35 L92 35 L92 58 L84 58 Z', loupe: [88, 46, 14] },
  WIDE_L:     { view: 'side',  shapes: OUTLINE.side },
  WIDE_R:     { view: 'side',  shapes: OUTLINE.side },
  F_BUMPER:   { view: 'front', shapes: 'M16 60 L124 60 L124 70 L16 70 Z M36 44 L104 44 L104 58 L36 58 Z' },
  ENGINE_BAY: { view: 'top',   shapes: 'M34 26 L116 26 L120 66 L30 66 Z' },
  HOOD:       { view: 'side',  shapes: 'M16 38 L60 35 L60 44 L16 47 Z' },
  HOOD_H:     { view: 'top',   shapes: 'M34 26 L116 26 L120 66 L30 66 Z' },
  ROOF:       { view: 'top',   shapes: 'M38 90 L112 90 L112 122 L38 122 Z' },
  DECK:       { view: 'top',   shapes: 'M38 126 L112 126 L112 138 L38 138 Z' },
  Q_LH_FRONT: { view: 'front', shapes: 'M18 74 L18 42 Q18 32 28 30 L46 27 L52 20 L52 74 Z M16 60 L62 60 L62 70 L16 70 Z' },
  Q_RH_FRONT: { view: 'front', shapes: 'M122 74 L122 42 Q122 32 112 30 L94 27 L88 20 L88 74 Z M78 60 L124 60 L124 70 L78 70 Z' },
  LAMP_LH:    { view: 'front', shapes: 'M20 43 L34 43 L34 55 L21 55 Z', loupe: [27, 49, 20] },
  LAMP_RH:    { view: 'front', shapes: 'M120 43 L106 43 L106 55 L119 55 Z', loupe: [113, 49, 20] },
  PANEL_LH:   { view: 'side',  shapes: 'M10 58 L10 44 Q10 39 16 38 L60 35 L60 58 Z' },
  PANEL_RH:   { view: 'side',  shapes: 'M10 58 L10 44 Q10 39 16 38 L60 35 L60 58 Z' },
};

export function VehicleView({ shotId, view }: { shotId: string; view: View }) {
  const target = TARGET[shotId];
  const v = target?.view ?? view;

  return (
    <svg viewBox={BOX[v]} className="vview" role="img" aria-hidden="true"
         preserveAspectRatio="xMidYMid meet">
      <g className="ln">
        <path d={OUTLINE[v]} />
        {DETAIL[v].map((d, i) => <path key={i} d={d} />)}
      </g>
      {target && (
        <>
          {target.shapes.split(' M').map((seg, i) => (
            <path key={i} className="hl" d={i === 0 ? seg : `M${seg}`} />
          ))}
          {target.loupe && (
            <circle className="loupe" cx={target.loupe[0]} cy={target.loupe[1]}
                    r={target.loupe[2]} />
          )}
        </>
      )}
    </svg>
  );
}
